import { DEFAULT_BACKUP_CULTURE } from '../../../util/culture';
import CUSTOM_EVENT from '../../../util/customEvents';
import { getLocalStorage, STORAGE_KEYS } from '../../../util/storage';
import { AUTH_COOKIE, getCookie } from '../../../util/cookies';

/**
 * Dispatches a custom event to notify that the compare key list has been updated.
 * @param {Array} compareKeyList - The updated compare key list array.
 * @param {boolean} isAtMaxComparisonAmount - inform the buttons if max compare number is reached.
 * so they can set their state accordingly.
 */
export function dispatchCompareKeyListUpdated(compareKeyList, isAtMaxComparisonAmount) {
  window.dispatchEvent(
    new CustomEvent(CUSTOM_EVENT.COMPARE_KEY_LIST_UPDATED, {
      detail: { compareKeyList, isAtMaxComparisonAmount },
    })
  );
}

/**
 * Fetches the package data information based on the chosen compare keys.
 *
 * @param {string} baseApi - The base API URL for fetching compare data.
 * @param {Function} setComparePackageList
 * @param {Function} setIsComparePackageListLoading
 */
export async function fetchComparePackageList(
  baseApi,
  setComparePackageList,
  setIsComparePackageListLoading
) {
  setIsComparePackageListLoading(true);

  const tourComparisonGetUrl = baseApi;

  const authToken = getCookie(AUTH_COOKIE);

  fetch(tourComparisonGetUrl, {
    method: 'GET',
    headers: {
      accept: 'application/json',
      ...(!!authToken && { authorization: `Bearer ${authToken}` }),
    },
    credentials: 'include',
  })
    .then((response) => {
      if (!response.ok) {
        throw new Error(`Network response was not ok`);
      }
      return response.text().then((text) => {
        // If empty response, return an array and don't return attempt to parse as JSON.
        const responseData = text ? JSON.parse(text) : [];
        return responseData;
      });
    })
    .then((responseWithInitialKeysList) => {
      setComparePackageList(responseWithInitialKeysList);
      setIsComparePackageListLoading(false);
    })
    .catch((error) => {
      console.error('Error fetching initial compare keys:', error);
      setIsComparePackageListLoading(false);
    });
}

/**
 * Fetches the compare key list by sending a GET request to the server and updates the state accordingly.
 *
 * @param {Function} setCompareKeyList - Function to update the compare key list state.
 * @param {Function} setIsCompareKeyListLoading - Function to set the loading state of the compare key list.
 * @param {string} baseApi - The base API URL for fetching the compare key list.
 * @param {number} maximumCompareAmount - set in cms - determines the amount of compare items set.
 */
export function getCompareKeyList(
  setCompareKeyList,
  setIsCompareKeyListLoading,
  baseApi,
  maximumCompareAmount
) {
  setIsCompareKeyListLoading(true);
  const url = `${baseApi}/keys`;
  const authToken = getCookie(AUTH_COOKIE);

  fetch(url, {
    method: 'GET',
    // Other environments require key not to be browser cached to ensure back button works correctly
    headers: {
      'Cache-Control': 'no-store',
      Pragma: 'no-cache',
      ...(!!authToken && { authorization: `Bearer ${authToken}` }),
    },
    cache: 'no-store',
  })
    .then((response) => {
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      if (response.status === 204) {
        return [];
      }
      return response.json();
    })
    .then((compareKeyListResponseData) => {
      setCompareKeyList(compareKeyListResponseData);
      setTimeout(() => {
        const isAtMaxComparisonAmount = compareKeyListResponseData.length >= maximumCompareAmount;
        dispatchCompareKeyListUpdated(compareKeyListResponseData, isAtMaxComparisonAmount);
        setIsCompareKeyListLoading(false);
      }, 0);
    })
    .catch((error) => {
      console.error('Error fetching compare key list:', error);
      setIsCompareKeyListLoading(false);
      alert('An error occurred. Please try again.');
    });
}

/**
 * Updates the compare list by sending a POST request to the server and updates the state accordingly.
 *
 * @param {string} key - The key of the item to be added or removed from the compare list.
 * @param {Function} setCompareKeyList - Function to update the compare key list state.
 * @param {Function} setIsCompareKeyListLoading - Function to set the loading state of the compare key list.
 * @param {string} baseApi - The base API URL for updating the compare list.
 * @param {Object} labels - The labels object containing various text strings for the UI.
 */
export function updateAndFetchCompareKeyList(
  key,
  setCompareKeyList,
  setIsCompareKeyListLoading,
  baseApi,
  labels,
  isAtMaxComparisonAmount
) {
  return new Promise((resolve, reject) => {
    const currentCulture = getLocalStorage(STORAGE_KEYS.currentCulture, DEFAULT_BACKUP_CULTURE);
    setIsCompareKeyListLoading(true);
    const url = `${baseApi}/${key}?culture=${currentCulture}`;
    const authToken = getCookie(AUTH_COOKIE);
    fetch(url, {
      method: 'POST',
      headers: {
        ...(!!authToken && { authorization: `Bearer ${authToken}` }),
      },
    })
      .then((response) => {
        if (response.status === 422) {
          alert(labels.multiLanguageDetectedValidationMessage);
          throw new Error(labels.multiLanguageDetectedValidationMessage);
        }
        return response.json();
      })
      .then((compareKeyListResponseData) => {
        setCompareKeyList(compareKeyListResponseData);
        dispatchCompareKeyListUpdated(compareKeyListResponseData, isAtMaxComparisonAmount);
        setIsCompareKeyListLoading(false);
        resolve();
      })
      .catch((error) => {
        console.error('Error updating compare list:', error);
        dispatchCompareKeyListUpdated(null);
        setIsCompareKeyListLoading(false);
        if (error.message !== labels.multiLanguageDetectedValidationMessage) {
          alert(labels.genericErrorMessageText);
        }
        reject(error);
      });
  });
}

/**
 * Removes all items from the compare list by sending a POST request to the server and updates the state accordingly.
 *
 * @param {string} baseApi
 */
export function removeAllKeysFromCompareKeyList(
  setCompareKeyList,
  setComparePackageList,
  baseApi,
  labels
) {
  const url = `${baseApi}/RemoveAll`;
  const authToken = getCookie(AUTH_COOKIE);
  fetch(url, {
    method: 'POST',
    headers: {
      ...(!!authToken && { authorization: `Bearer ${authToken}` }),
    },
  })
    .then(() => {
      setCompareKeyList([]);
      setComparePackageList([]);
      dispatchCompareKeyListUpdated([]);
    })
    .catch((error) => {
      console.error('Error removing compare list:', error);
      alert(labels.genericErrorMessageText);
    });
}

/**
 * Scrolls to the previous item in a list using refs, with an additional offset.
 *
 * @param {Array} itemRefs - An array of refs corresponding to the list items.
 * @param {Array} itemList - The list of items being compared.
 * @param {string} itemKey - The key of the item that was removed.
 * @param {HTMLElement} container - The scrollable container element.
 * @param {number} offset - The additional offset to apply when scrolling in pixels.
 */
export const scrollToPreviousItem = (itemRefs, itemList, itemKey, container, offset = 0) => {
  const index = itemList.findIndex((item) => item.key === itemKey);
  if (index > 0) {
    const previousItem = itemRefs.current[index - 1];
    if (previousItem && container.current) {
      const containerWidth = container.current.clientWidth;
      const itemLeft = previousItem.getBoundingClientRect().left;
      const containerLeft = container.current.getBoundingClientRect().left;
      const xOffset = -offset;

      // Calculate the new scroll position
      const x = itemLeft - containerLeft + container.current.scrollLeft + xOffset;

      // Ensure the item is centered in the view
      const centeredX = x - containerWidth / 2 + previousItem.clientWidth / 2;

      container.current.scrollTo({ left: centeredX, behavior: 'smooth' });
    }
  }
};
